import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'
import { buildApiUrl } from '@/api/client'
import { firebaseAuth } from '@/config/firebase'
import { getLocale, getTimezone, type AppLocale } from '@/config/locale'
import { clearPendingAuthEmail } from '@/auth/storage'

const nullableString = z.string().nullable().optional()

const teamSchema = z
  .object({
    id: z.number(),
    name: nullableString,
    display_name: nullableString,
    short_name: nullableString,
    logo_url: nullableString,
    is_placeholder: z.boolean().optional(),
  })
  .passthrough()

const rankSchema = z
  .object({
    id: z.number().optional(),
    name: nullableString,
    icon: nullableString,
    color: nullableString,
    min_points: z.number().optional(),
    max_points: z.number().optional(),
  })
  .passthrough()

const pointsDashboardSchema = z
  .object({
    total_points: z.number().optional(),
    active_poules: z.number().optional(),
  })
  .passthrough()

const currentUserSchema = z
  .object({
    id: z.number(),
    email: nullableString,
    display_name: nullableString,
    name: nullableString,
    first_name: nullableString,
    last_name: nullableString,
    preferred_locale: nullableString,
    profile_image: nullableString,
    is_admin: z.boolean().optional(),
    favorite_team: teamSchema.nullable().optional(),
    last_login_at: nullableString,
    rank_score: z.number().optional(),
    rank: rankSchema.nullable().optional(),
    is_profile_complete: z.boolean().optional(),
    user_points_dashboard: pointsDashboardSchema.optional(),
  })
  .passthrough()

const wrappedCurrentUserSchema = z
  .object({
    data: currentUserSchema,
    profile_complete: z.boolean().optional(),
  })
  .passthrough()

const validationErrorResponseSchema = z
  .object({
    message: z.string(),
    errors: z.record(z.array(z.string())).optional(),
  })
  .passthrough()

const apiErrorResponseSchema = z
  .object({
    message: z.string().optional(),
    error_code: z.string().optional(),
    code: z.string().optional(),
    errors: z.record(z.array(z.string())).optional(),
  })
  .passthrough()

export type CurrentUser = z.infer<typeof currentUserSchema>
export type ValidationErrorResponse = z.infer<typeof validationErrorResponseSchema>
export type ApiErrorResponse = z.infer<typeof apiErrorResponseSchema>

export interface AuthenticatedUserBasicResult {
  userWithStats: CurrentUser
  isProfileComplete: boolean | null
}

export interface CompleteRegistrationPayload {
  email: string
  display_name: string
  favorite_team_id?: number | null
  preferred_locale?: AppLocale
  accepted_terms: '1' | '0'
  turnstile_token?: string | undefined
}

export class AuthenticatedUserApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: ApiErrorResponse | null,
    public readonly responseText: string,
  ) {
    super(body?.message?.trim() || `Backend request failed (${status}).`)
    this.name = 'AuthenticatedUserApiError'
  }

  get apiCode() {
    const code = this.body?.error_code ?? this.body?.code
    return code?.trim().toUpperCase() || null
  }

  get isExplicitUserMissing() {
    return (
      this.status === 404 ||
      this.apiCode === 'USER_NOT_FOUND' ||
      this.apiCode === 'PROFILE_NOT_FOUND'
    )
  }
}

export class RegistrationValidationError extends Error {
  constructor(
    public override readonly message: string,
    public readonly errors: Record<string, string[]>,
  ) {
    super(message)
    this.name = 'RegistrationValidationError'
  }

  get displayNameError() {
    const first = this.errors.display_name?.[0]?.trim()
    return first || null
  }
}

function parseCurrentUserPayload(response: unknown) {
  const wrapped = wrappedCurrentUserSchema.safeParse(response)
  if (wrapped.success) {
    return {
      user: wrapped.data.data,
      profileComplete: extractProfileComplete(wrapped.data),
    }
  }

  const unwrapped = currentUserSchema.safeParse(response)
  if (unwrapped.success) {
    return {
      user: unwrapped.data,
      profileComplete: extractProfileComplete(unwrapped.data),
    }
  }

  console.error('[user] Invalid current-user payload', {
    wrapped: wrapped.error.flatten(),
    unwrapped: unwrapped.error.flatten(),
  })
  throw new Error('Gebruiker kon niet worden geladen.')
}

function parseApiErrorResponse(responseText: string) {
  const trimmed = responseText.trim()
  if (!trimmed) return null

  try {
    const parsed = apiErrorResponseSchema.safeParse(JSON.parse(trimmed))
    return parsed.success ? parsed.data : null
  } catch {
    return null
  }
}

function getBearerHeaders(token: string) {
  return {
    Accept: 'application/json',
    'Content-Type': 'application/json; charset=UTF-8',
    Authorization: `Bearer ${token}`,
    'X-Timezone': getTimezone(),
    'X-Locale': getLocale(),
    'X-Platform': 'web',
  }
}

async function readResponseText(response: Response) {
  try {
    return await response.text()
  } catch {
    return ''
  }
}

async function ensureAuthenticatedToken() {
  const currentUser = firebaseAuth.currentUser
  if (!currentUser) {
    throw new Error('Geen Firebase-gebruiker beschikbaar.')
  }

  const token = await currentUser.getIdToken()
  if (!token) {
    throw new Error('Geen Firebase-token beschikbaar.')
  }

  return token
}

function throwRegistrationValidation(responseText: string): never {
  const parsed = validationErrorResponseSchema.safeParse(JSON.parse(responseText))
  if (!parsed.success) {
    throw new Error('Registratievalidatie kon niet worden gelezen.')
  }

  throw new RegistrationValidationError(parsed.data.message, parsed.data.errors ?? {})
}

async function fetchCurrentUser(): Promise<CurrentUser> {
  const token = await ensureAuthenticatedToken()
  const { user } = await fetchAuthenticatedUser(token)
  return user
}

export function extractProfileComplete(response: unknown) {
  if (!response || typeof response !== 'object') return null

  if ('is_profile_complete' in response) {
    const value = (response as { is_profile_complete?: unknown }).is_profile_complete
    if (typeof value === 'boolean') return value
  }

  if ('data' in response && response.data && typeof response.data === 'object') {
    const value = (response.data as { is_profile_complete?: unknown }).is_profile_complete
    if (typeof value === 'boolean') return value
  }

  if ('profile_complete' in response) {
    const value = (response as { profile_complete?: unknown }).profile_complete
    if (typeof value === 'boolean') return value
  }

  return null
}

export async function fetchAuthenticatedUser(token: string) {
  const response = await fetch(buildApiUrl('/users/me'), {
    headers: getBearerHeaders(token),
  })

  const responseText = await readResponseText(response)

  if (!response.ok) {
    throw new AuthenticatedUserApiError(
      response.status,
      parseApiErrorResponse(responseText),
      responseText,
    )
  }

  const payload = responseText ? (JSON.parse(responseText) as unknown) : {}
  return parseCurrentUserPayload(payload)
}

export async function fetchAuthenticatedUserBasicResult(
  token: string,
): Promise<AuthenticatedUserBasicResult> {
  const { user, profileComplete } = await fetchAuthenticatedUser(token)

  return {
    userWithStats: profileComplete == null ? user : { ...user, is_profile_complete: profileComplete },
    isProfileComplete: profileComplete,
  }
}

export async function completeRegistration(payload: CompleteRegistrationPayload) {
  const token = await ensureAuthenticatedToken()
  const response = await fetch(buildApiUrl('/users'), {
    method: 'POST',
    headers: getBearerHeaders(token),
    body: JSON.stringify(payload),
  })

  const responseText = await readResponseText(response)

  if (response.status === 422) {
    throwRegistrationValidation(responseText)
  }

  if (!response.ok) {
    throw new AuthenticatedUserApiError(
      response.status,
      parseApiErrorResponse(responseText),
      responseText,
    )
  }

  clearPendingAuthEmail()

  const payloadJson = responseText ? (JSON.parse(responseText) as unknown) : {}
  return parseCurrentUserPayload(payloadJson).user
}

export function mapRegistrationValidation(error: unknown) {
  if (error instanceof RegistrationValidationError) {
    return {
      message: error.message,
      errors: error.errors,
      displayNameError: error.displayNameError,
    }
  }

  return null
}

export function useCurrentUser(enabled = true) {
  return useQuery({
    queryKey: ['current-user'],
    queryFn: fetchCurrentUser,
    enabled,
    staleTime: 60_000,
  })
}

export function getDisplayLabel(user?: Pick<CurrentUser, 'display_name' | 'name'> | null) {
  const displayName = user?.display_name?.trim()
  if (displayName) return displayName

  const fallbackName = user?.name?.trim()
  if (fallbackName) return fallbackName

  return 'Speler'
}

export function getGreetingName(
  user?: Pick<CurrentUser, 'display_name' | 'first_name'> | null,
) {
  const displayName = user?.display_name?.trim()
  if (displayName) {
    const firstToken = displayName.split(/\s+/)[0]?.trim()
    if (firstToken) return firstToken
  }

  const firstName = user?.first_name?.trim()
  if (firstName) return firstName

  return 'daar'
}
